import { useState, useRef, useMemo, useCallback } from 'react';
import { Listbox } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { Mixpanel } from '../../../services/Mixpanel';
import PremiumOfferCta from '../../../components/PremiumOfferCta';
import { usePremiumOffer } from '../../../contexts/premiumOffer';
import PremiumIconCrown from '../../../components/PremiumIconCrown';
import { useDiffitUserContext } from '../../../contexts/DiffitUserContext';

/**
 * Dropdown for selecting a chapter, with premium chapter gating for free users.
 * @param {Object} props
 * @param {Array} props.options - List of chapter option objects to display.
 * @param {string|null} props.selected - Currently selected chapter label, or null if none.
 * @param {string} props.placeholder - Placeholder text when no chapter is selected.
 * @param {Function} props.onChange - Callback invoked with the selected option object.
 */
export const ChapterDropdown = ({ options, selected, placeholder, onChange }) => {
  const { isOffered } = usePremiumOffer();
  const buttonRef = useRef(null);
  const [openUp, setOpenUp] = useState(false);
  const { isPremiumUser } = useDiffitUserContext();

  const checkDirection = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    setOpenUp(spaceBelow < 200);
  }, []);

  // Build display options with premium row divider for free users
  const displayOptions = useMemo(() => {
    if (isPremiumUser) return options;
    const newOptions = [...options];
    const firstPremiumIdx = newOptions.findIndex((item) => item.isPremium);
    if (firstPremiumIdx !== -1) {
      newOptions.splice(firstPremiumIdx, 0, {
        type: 'premium',
        message: 'Get unlimited access',
      });
    }
    return newOptions;
  }, [options, isPremiumUser]);

  return (
    <Listbox value={selected} onChange={onChange}>
      {() => (
        <div className='relative'>
          <Listbox.Button
            ref={buttonRef}
            onClick={checkDirection}
            className='w-full px-4 py-3 border border-gray-300 rounded-xl text-left flex items-center justify-between hover:bg-gray-50 transition-colors'>
            <span className={selected ? 'text-gray-900 truncate' : 'text-gray-400'}>
              {selected || placeholder}
            </span>
            <ChevronDownIcon className='w-4 h-4 text-gray-400 flex-shrink-0' />
          </Listbox.Button>
          <Listbox.Options
            className={`absolute z-[60] w-full bg-white border border-gray-300 rounded-xl shadow-lg max-h-48 overflow-auto scrollbar-minimal ${
              openUp ? 'bottom-full mb-1' : 'top-full mt-1'
            }`}>
            {displayOptions.map((option, idx) => {
              // Premium divider row
              if (option.type === 'premium') {
                return (
                  <Listbox.Option
                    key={`premium-${idx}`}
                    value={option}
                    disabled
                    className='px-3 py-2 bg-gray-100'>
                    <div className='flex items-center gap-x-2'>
                      <PremiumIconCrown wrapperClassName='flex-shrink-0' className='h-3 w-3' />
                      <div>
                        <span className='font-semibold text-xs'>{option.message}</span>
                        {isOffered ? (
                          <PremiumOfferCta
                            variant='stacked'
                            location='Book Chapter Dropdown'
                            planLabel='See plans'
                          />
                        ) : (
                          <a
                            href='https://web.diffit.me/plans'
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-amber-700 text-xs hover:underline flex items-center gap-x-1 font-semibold'
                            onClick={() => {
                              Mixpanel.track('See plans clicked', {
                                location: 'Unlimited Book Modal Chapter Dropdown',
                                message: option.message,
                              });
                            }}>
                            See plans
                            <ChevronRightIcon className='h-3 w-3' />
                          </a>
                        )}
                      </div>
                    </div>
                  </Listbox.Option>
                );
              }

              const isDisabled = option.isPremium && !isPremiumUser;

              return (
                <Listbox.Option
                  key={idx}
                  value={option}
                  disabled={isDisabled}
                  className={({ active }) =>
                    `px-3 py-1.5 truncate text-sm ${active ? 'bg-gray-50' : ''} ${
                      isDisabled ? 'cursor-default bg-gray-100' : 'cursor-pointer'
                    }`
                  }>
                  <span
                    className={`flex items-center justify-between ${
                      isDisabled ? 'text-gray-400' : ''
                    }`}>
                    {option.name}
                    {option.isPremium && !isPremiumUser && (
                      <PremiumIconCrown
                        wrapperClassName='shrink-0'
                        className='h-3 w-3 ml-4'
                        toolTipProps={{
                          text: undefined,
                          position: 'top',
                          anchor: 'right',
                        }}
                      />
                    )}
                  </span>
                </Listbox.Option>
              );
            })}
          </Listbox.Options>
        </div>
      )}
    </Listbox>
  );
};
